import type { StoreCartShippingOption } from "@medusajs/types";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { useCallback, useMemo } from "react";
import { reconcileShippingSelection } from "@/utils/shippingOption";
import { deriveFreeShippingThreshold } from "./freeShipping";
import { useAddCartShippingMethod } from "./mutations";
import { useCartShippingOptions } from "./queries";
import {
  useRearmShippingAutoSelect,
  useStandardShippingAutoSelect
} from "./useStandardShippingAutoSelect";

type ShippingMethod =
  SalonStockerHttpTypes.StoreSellerCart["shipping_methods"][number];

export interface UseShippingMethodSyncOptions {
  /**
   * The cart ID to sync shipping methods for
   */
  cartId: string;
  /**
   * The cart's currently selected shipping method (`cart.shipping_methods[0]`)
   */
  selectedMethod?: ShippingMethod | null;
  /**
   * Set by the views that render a shipping selector (Cart view, checkout):
   * mounting one re-arms a failed auto-select attempt for this cart - see
   * `useRearmShippingAutoSelect`. The app-shell watcher leaves it off.
   */
  rearmAutoSelect?: boolean;
}

export interface UseShippingMethodSyncResult {
  /**
   * Available shipping options for this cart
   */
  options: Array<{
    id: string;
    name: string;
    amount?: number | null;
  }>;
  /**
   * The id in `options` the cart's method is selected as, if any - see
   * `reconcileShippingSelection`
   */
  selectedOptionId?: string;
  /**
   * Whether shipping options are loading
   */
  isLoading: boolean;
  /**
   * Whether a shipping method change is pending
   */
  isPending: boolean;
  /**
   * Whether the currently selected option is valid
   */
  isSelectionValid: boolean;
  /**
   * Change the selected shipping method
   */
  selectOption: (optionId: string) => Promise<void>;

  freeShippingThreshold?: number;
}

/**
 * Hook for managing shipping method selection UI.
 *
 * This hook provides:
 * - Available shipping options for a cart
 * - The option the cart's method is selected as, and whether it is valid
 * - Selection handler
 * - Auto-selection of the "Standard" shipping option whenever the cart has no
 *   valid selection for the option list it is currently offered
 *
 * @example
 * ```tsx
 * const { options, selectedOptionId, isSelectionValid, selectOption } =
 *   useShippingMethodSync({
 *     cartId: cart.cart_id,
 *     selectedMethod: cart.shipping_methods[0]
 *   });
 * ```
 */
export function useShippingMethodSync({
  cartId,
  selectedMethod,
  rearmAutoSelect = false
}: UseShippingMethodSyncOptions): UseShippingMethodSyncResult {
  useRearmShippingAutoSelect(cartId, rearmAutoSelect);
  const { data, isLoading, isFetching } = useCartShippingOptions(cartId);
  const addShippingMethod = useAddCartShippingMethod(cartId);
  // Destructure so callbacks below can depend on stable pieces rather than the
  // mutation result object, which useMutation rebuilds every render - keyed on
  // the whole object, selectOption would change identity each render and
  // defeat memo() on every ShippingMethodSelect consuming it.
  const {
    mutate: addShippingMethodMutate,
    mutateAsync: addShippingMethodMutateAsync,
    isPending: isAddingShippingMethod
  } = addShippingMethod;

  const listedOptions: StoreCartShippingOption[] = useMemo(
    () => data?.shipping_options ?? [],
    [data]
  );

  const {
    options,
    selectedOptionId,
    isStale: isSelectionStale
  } = useMemo(
    () => reconcileShippingSelection(listedOptions, selectedMethod),
    [listedOptions, selectedMethod]
  );

  const isSelectionValid = !isSelectionStale;

  const selectOption = useCallback(
    async (optionId: string) => {
      if (
        !optionId ||
        optionId === selectedOptionId ||
        isAddingShippingMethod
      ) {
        return;
      }
      // Name and price travel with the id so the cache can be patched before
      // the round trip - the selector reads its value from cart data, so
      // without them it would keep showing the old method until the server
      // answered. See `useAddCartShippingMethod`.
      const option = options.find((opt) => opt.id === optionId);
      await addShippingMethodMutateAsync({
        optionId,
        name: option?.name ?? "",
        amount: option?.amount ?? 0
      });
    },
    [
      selectedOptionId,
      isAddingShippingMethod,
      addShippingMethodMutateAsync,
      options
    ]
  );

  // Auto-select "Standard" when the cart has no valid method for this list.
  // The rule and its one-attempt-per-list guard are shared with the app-shell
  // watcher (KAN-267), so both can be mounted without double-posting.
  useStandardShippingAutoSelect({
    cartId,
    selectedOptionId,
    isSelectionValid,
    isLoading,
    isFetching,
    isAddingShippingMethod,
    listedOptions,
    addShippingMethod: addShippingMethodMutate
  });

  // Derive the free-shipping threshold from the cart's shipping options. The
  // shape it reads is a `0` price gated behind an `item_total >= X` rule, e.g.
  // { currency_code: "aud", amount: 0, price_rules: [
  //     { attribute: "item_total", value: "100", operator: "gte", ... } ] }.
  // Shared with the cart-view meter so the two stay in lockstep. Memoized:
  // the scan is O(options x prices) and this hook renders once per seller row.
  const freeShippingThreshold = useMemo(
    () => deriveFreeShippingThreshold(listedOptions),
    [listedOptions]
  );

  return {
    options,
    selectedOptionId,
    // An empty list that is being refetched is not an answer yet. Medusa lists
    // a cart's options by the shipping profiles of its items, so a cart with no
    // items quotes none - and a cart emptied by a supplier switch has that
    // empty list cached, then gets reused when a product is switched back to
    // it. The cached `[]` would otherwise render "No shipping options" (and
    // "Not selected" upstream) for the whole refetch (KAN-260).
    isLoading: isLoading || (isFetching && listedOptions.length === 0),
    isPending: isAddingShippingMethod,
    isSelectionValid,
    selectOption,
    freeShippingThreshold
  };
}
